import clsx from 'clsx';
import { FC, ReactNode } from 'react';

import { IconAncap } from 'src/general/Icons/IconAncap';
import { IconCalendar } from 'src/general/Icons/IconCalendar';
import { IconCalendarWithTick } from 'src/general/Icons/IconCalendarWithTick';
import { IconColour } from 'src/general/Icons/IconColour';
import { IconDriveType } from 'src/general/Icons/IconDriveType';
import { IconEngine } from 'src/general/Icons/IconEngine';
import { IconEnginePower } from 'src/general/Icons/IconEnginePower';
import { IconFuelConsumption } from 'src/general/Icons/IconFuelConsumption';
import { IconNumberOfKeys } from 'src/general/Icons/IconNumberOfKeys';
import { IconOdometer } from 'src/general/Icons/IconOdometer';
import { IconPetrol } from 'src/general/Icons/IconPetrol';
import { IconSeats } from 'src/general/Icons/IconSeats';
import { IconSpanner } from 'src/general/Icons/IconSpanner';
import { IconTransmission } from 'src/general/Icons/IconTransmission';
import { IconVIN } from 'src/general/Icons/IconVIN';

import styles from './CarKeyInformation.module.scss';
import { KEY_ITEMS } from './CarKeyInformation.type';

interface Item {
  key: KEY_ITEMS;
  value: string;
}

interface Props {
  items: Item[];
}

type KeyItemProps = {
  [key in KEY_ITEMS]: {
    icon: ReactNode;
    label: string;
  };
};

const keyItem: KeyItemProps = {
  [KEY_ITEMS.KILOMETERS]: {
    icon: <IconOdometer />,
    label: 'Kilometres',
  },
  [KEY_ITEMS.COMPLIANCE_YEAR]: {
    icon: <IconCalendarWithTick />,
    label: 'Compliance year',
  },
  [KEY_ITEMS.BUILD_YEAR]: {
    icon: <IconCalendar />,
    label: 'Build year',
  },
  [KEY_ITEMS.ENGINE_SIZE]: {
    icon: <IconEngine />,
    label: 'Engine',
  },
  [KEY_ITEMS.FUEL_TYPE]: {
    icon: <IconPetrol />,
    label: 'Fuel type',
  },
  [KEY_ITEMS.TRANSMISSION]: {
    icon: <IconTransmission />,
    label: 'Drivetrain',
  },
  [KEY_ITEMS.DRIVE_TYPE]: {
    icon: <IconDriveType />,
    label: 'Drive type',
  },
  [KEY_ITEMS.SEATS]: {
    icon: <IconSeats />,
    label: 'Seats',
  },
  [KEY_ITEMS.LAST_SERVICE_DATE]: {
    icon: <IconSpanner />,
    label: 'Last service date',
  },
  [KEY_ITEMS.VIN_NUMBER]: {
    icon: <IconVIN />,
    label: 'VIN',
  },
  [KEY_ITEMS.FUEL_CONSUMPTION]: {
    icon: <IconFuelConsumption />,
    label: 'Fuel consumption',
  },
  [KEY_ITEMS.ANCAP_SAFETY_RATING]: {
    icon: <IconAncap />,
    label: 'ANCAP safety rating',
  },
  [KEY_ITEMS.KEYS]: {
    icon: <IconNumberOfKeys />,
    label: 'Number of keys',
  },
  [KEY_ITEMS.EXTERIOR_COLOR]: {
    icon: <IconColour />,
    label: 'Exterior colour',
  },
  [KEY_ITEMS.ENGINE_POWER]: {
    icon: <IconEnginePower />,
    label: 'Engine power',
  },
};

export const CarKeyInformation: FC<Props> = ({ items }) => (
  <section className={styles.root}>
    <dl className={styles.layout}>
      {items.length !== 0 &&
        items.map(({ value, key }) => (
          <div
            className={clsx({
              [styles.infoWithVin]: items.find(({ key }) => key === KEY_ITEMS.VIN_NUMBER),
              [styles.info]: items.find(({ key }) => key !== KEY_ITEMS.VIN_NUMBER),
            })}
            key={key}
          >
            <div className={styles.infoIcon} aria-hidden="true">
              {keyItem[key].icon}
            </div>
            <dt className={styles.infoName}>{keyItem[key].label}</dt>
            <dd className={styles.infoValue} data-testid={`CarKeyInformation-component-${key}-info`}>
              {value}
            </dd>
          </div>
        ))}
    </dl>
  </section>
);
